import React from 'react';
import { ArrowRight, Bell, Building2, Fuel } from 'lucide-react';

import { cn, formatCompactCurrency, formatCompactNumber } from '@/lib/utils';

/**
 * « Vos autres activités »: ce que l'accueil transport montre des autres
 * domaines de la licence (stations, contrôle du carburant), un panneau par
 * domaine, trois chiffres au plus, le reste derrière « Voir le détail ».
 *
 * Il remplace une grille de quinze tuiles en capitales qui mélangeait les
 * domaines, répétait le stock et les alertes déjà visibles plus haut et
 * affichait une marge station de −642 millions calculée sur des achats que
 * les ventes ne couvraient pas encore.
 *
 * Trois règles:
 *  1. Un domaine n'apparaît que si le pack le contient ET que l'un de ses
 *     modules figure explicitement dans la liste des modules de l'utilisateur
 *     (licence restreinte par ses droits). Liste absente = rien: on ne devine
 *     pas une licence. Le pack Transport et distribution n'en contient aucun,
 *     donc la section disparaît, même si le serveur renvoie des chiffres.
 *  2. Aucun chiffre déjà en haut de l'accueil (stock, créances, trajets,
 *     convois): le stock consolidé, les missions à risque et l'alerte
 *     « anomalies groupe » (le même compte que les alertes ouvertes) sortent.
 *  3. Un montant qu'on sait faux n'est pas affiché: « À compléter » et un lien
 *     vers l'écran qui permet de le corriger.
 */

/** Domaines que chaque pack peut ouvrir en plus du transport. */
const DOMAINES_PAR_PACK = {
  TRANSPORT_DISTRIBUTION: [],
  STATIONS_RESEAU: ['stations'],
  CONTROLE_CARBURANT: ['control_fuel'],
  ENTERPRISE: ['stations', 'control_fuel'],
};

const MODULES_DU_DOMAINE = {
  stations: ['stations', 'station_pos', 'station_sales', 'station_kpis', 'purchases'],
  control_fuel: ['fuel_requests', 'fuel_issues', 'fuel_consumption', 'sites', 'assets'],
};

const estPositif = (valeur) => Number(valeur) > 0;

/**
 * Les domaines à montrer, dans l'ordre d'affichage. `group` (les alertes
 * transverses) ne vient qu'avec au moins un autre domaine: seul, il ne
 * redirait que ce que l'accueil transport montre déjà.
 */
export const domainesVisibles = ({ activePack, enabledModules }) => {
  if (!Array.isArray(enabledModules)) return [];
  const actifs = new Set(enabledModules);
  const permis = DOMAINES_PAR_PACK[activePack] || [];
  const domaines = permis.filter((domaine) =>
    MODULES_DU_DOMAINE[domaine].some((code) => actifs.has(code)),
  );
  if (domaines.length > 0 && actifs.has('alerts')) domaines.push('group');
  return domaines;
};

// Lecture par clé stable (`metric.key`); repli sur l'ancien libellé quand le
// serveur ne renvoie pas encore de clé.
const metrique = (section, cle, libelleRepli) => {
  const metrics = section?.metrics || [];
  return (
    metrics.find((m) => m?.key === cle) ||
    (libelleRepli ? metrics.find((m) => !m?.key && m?.label === libelleRepli) : undefined)
  );
};
const mission = (section, titre) =>
  (section?.missions || []).find((m) => m?.title === titre);

const nombreOuNull = (valeur) => {
  const n = Number(valeur);
  return valeur === null || valeur === undefined || !Number.isFinite(n) ? null : n;
};

const formatLitresCompact = (n) => `${formatCompactNumber(Math.round(n))} L`;
const formatEntier = (n) => Math.round(n).toLocaleString('fr-FR');
const formatArgent = (n) => formatCompactCurrency(Math.round(n));

/**
 * Description des panneaux. Chaque chiffre lit une métrique du serveur par
 * son libellé; `si` filtre (un compte nul de « cuves sous le seuil » n'a rien
 * à dire), `fiable` décide si le montant peut être montré tel quel.
 */
const PANNEAUX = {
  stations: {
    titre: 'Stations',
    route: '/stations',
    icone: Building2,
    chiffres: [
      {
        id: 'cuves-sous-seuil',
        source: 'tanks_at_risk',
        repli: 'Cuves à risque',
        libelle: 'Cuves sous le seuil',
        format: formatEntier,
        si: estPositif,
        ton: 'critique',
      },
      { id: 'ventes', source: 'sales_value',
        repli: 'Ventes', libelle: 'Ventes', format: formatArgent, argent: true },
      {
        id: 'marge-brute',
        source: 'gross_margin',
        repli: 'Marge brute',
        libelle: 'Marge brute',
        format: formatArgent,
        argent: true,
        // Ventes moins achats sur toute la période: tant que les achats
        // rentrés en cuve ne sont pas vendus, l'écart n'est pas une marge.
        // Au-delà de ±100 % des ventes, le chiffre est faux, pas mauvais.
        fiable: (valeur, section) => {
          const ventes = nombreOuNull(metrique(section, 'sales_value', 'Ventes')?.value);
          return ventes !== null && ventes > 0 && Math.abs(valeur) <= ventes;
        },
        aCompleter: {
          route: '/stations/purchases',
          aide: 'Achats et ventes des stations à rapprocher',
        },
      },
      { id: 'litres-vendus', source: 'sales_liters',
        repli: 'Litres vendus', libelle: 'Litres vendus', format: formatLitresCompact },
    ],
  },
  control_fuel: {
    titre: 'Contrôle du carburant',
    route: '/control-fuel',
    icone: Fuel,
    chiffres: [
      {
        id: 'demandes-a-valider',
        source: 'pending_requests',
        repli: 'Demandes à valider',
        libelle: 'Demandes à valider',
        format: formatEntier,
        si: estPositif,
        ton: 'attention',
        route: '/control-fuel/requests',
      },
      { id: 'litres-sortis', source: 'issued_liters',
        repli: 'Litres sortis', libelle: 'Litres sortis', format: formatLitresCompact },
      {
        id: 'depense-interne',
        source: 'internal_spend',
        repli: 'Dépense interne',
        libelle: 'Dépense interne',
        format: formatArgent,
        argent: true,
        // Une sortie sans prix compte zéro franc: la somme est fausse tant
        // qu'il en reste une.
        fiable: (valeur, section) =>
          !(Number(mission(section, 'Sorties sans prix')?.count) > 0),
        aCompleter: {
          route: '/control-fuel/issues',
          aide: 'Des sorties de carburant n’ont pas de prix',
        },
      },
    ],
  },
  group: {
    titre: 'Alertes',
    route: '/alerts',
    icone: Bell,
    chiffres: [
      {
        id: 'alertes-ouvertes',
        source: 'open_alerts',
        repli: 'Alertes ouvertes',
        libelle: 'Alertes ouvertes',
        format: formatEntier,
        ton: 'critique',
      },
    ],
  },
};

const MAX_CHIFFRES = 3;

/**
 * Construit les panneaux à afficher à partir de la réponse du cockpit.
 * Fonction pure, testée seule.
 */
export const construirePanneaux = ({ pilotage, activePack, enabledModules, canSeeMoney }) => {
  const sections = Array.isArray(pilotage?.sections) ? pilotage.sections : [];
  const parCle = Object.fromEntries(sections.map((s) => [s?.key, s]));

  return domainesVisibles({ activePack, enabledModules })
    .map((domaine) => {
      const def = PANNEAUX[domaine];
      const section = parCle[domaine];
      if (!def || !section) return null;

      const chiffres = def.chiffres
        .filter((c) => !c.argent || canSeeMoney)
        .map((c) => {
          const m = metrique(section, c.source, c.repli);
          // Le serveur signale lui-même un chiffre incomplet (valeur nulle,
          // `incomplete`): « à compléter », jamais un zéro ni un trou.
          if (m?.incomplete && c.aCompleter) {
            return {
              id: `${domaine}-${c.id}`,
              libelle: c.libelle,
              texte: null,
              aCompleter: c.aCompleter,
              ton: null,
              route: c.route || null,
            };
          }
          const valeur = nombreOuNull(m?.value);
          if (valeur === null) return null;
          if (c.si && !c.si(valeur)) return null;
          const fiable = c.fiable ? c.fiable(valeur, section) : true;
          return {
            id: `${domaine}-${c.id}`,
            libelle: c.libelle,
            texte: fiable ? c.format(valeur) : null,
            aCompleter: fiable ? null : c.aCompleter,
            ton: c.ton && valeur > 0 ? c.ton : null,
            route: c.route || null,
          };
        })
        .filter(Boolean)
        .slice(0, MAX_CHIFFRES);

      if (chiffres.length === 0) return null;
      return { cle: domaine, titre: def.titre, route: def.route, icone: def.icone, chiffres };
    })
    .filter(Boolean);
};

const TON_VALEUR = {
  critique: 'text-destructive',
  attention: 'text-flame-ink',
};

const Panneau = ({ panneau, onNavigate }) => {
  const Icone = panneau.icone;
  return (
    <section
      aria-labelledby={`autres-${panneau.cle}`}
      className="flex h-full flex-col rounded-2xl border border-border/70 bg-card px-3 py-2.5 shadow-sm"
      data-testid={`domaine-${panneau.cle}`}
    >
      <header className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            aria-hidden="true"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"
          >
            <Icone className="h-4 w-4" />
          </span>
          <h3 id={`autres-${panneau.cle}`} className="truncate text-sm font-bold text-foreground">
            {panneau.titre}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => onNavigate(panneau.route)}
          className="group inline-flex shrink-0 items-center gap-1 rounded-lg px-1.5 py-0.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Voir le détail
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
      </header>

      <dl className="mt-2 divide-y divide-border/60">
        {panneau.chiffres.map((chiffre) => (
          <div
            key={chiffre.id}
            className="flex items-baseline justify-between gap-3 py-1.5"
            data-testid={`chiffre-${chiffre.id}`}
          >
            <dt className="min-w-0 truncate text-xs text-muted-foreground">{chiffre.libelle}</dt>
            <dd className="shrink-0 text-right">
              {chiffre.texte !== null ? (
                chiffre.route ? (
                  <button
                    type="button"
                    onClick={() => onNavigate(chiffre.route)}
                    className={cn(
                      'text-sm font-bold tabular-nums hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      TON_VALEUR[chiffre.ton] || 'text-foreground',
                    )}
                  >
                    {chiffre.texte}
                  </button>
                ) : (
                  <span
                    className={cn(
                      'text-sm font-bold tabular-nums',
                      TON_VALEUR[chiffre.ton] || 'text-foreground',
                    )}
                  >
                    {chiffre.texte}
                  </span>
                )
              ) : (
                <button
                  type="button"
                  onClick={() => onNavigate(chiffre.aCompleter.route)}
                  title={chiffre.aCompleter.aide}
                  aria-label={`${chiffre.libelle} : à compléter. ${chiffre.aCompleter.aide}`}
                  className="text-xs font-semibold text-flame-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  À compléter
                </button>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export const AutresActivites = ({ panneaux = [], onNavigate, className }) => {
  if (!panneaux.length) return null;
  return (
    <section
      aria-labelledby="autres-activites"
      className={cn('space-y-2', className)}
      data-testid="autres-activites"
    >
      <h2
        id="autres-activites"
        className="font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
      >
        Vos autres activités
      </h2>
      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3">
        {panneaux.map((panneau) => (
          <Panneau key={panneau.cle} panneau={panneau} onNavigate={onNavigate} />
        ))}
      </div>
    </section>
  );
};

export default AutresActivites;
